/**
 * Component slot schema — owned by the Components domain, the single authority
 * for block materialization.
 *
 * Consumed by Pages (scalarSlots → block form fields + scalar attributes;
 * repeatSlots → repeated item editors) and Media (mediaSlots[].targets → crop
 * target dimensions). Every consumer imports this shape so request/response
 * payloads and stored blocks all agree.
 */
import { DEFAULT_ACCEPTS, } from "./media-type";
/** Materialize the scalar values a newly-created block carries. */
export function defaultScalarValues(schema) {
    return Object.fromEntries(schema.scalarSlots
        .filter((slot) => slot.defaultValue !== undefined)
        .map((slot) => [slot.key, slot.defaultValue]));
}
/**
 * Does the layout the Editor has chosen display this image slot?
 *
 * A component that declares no layout displays everything it declares. An unset
 * or unrecognised layout value reads as the fallback — the same coercion the
 * renderer applies — so what the Editor is told and what the page renders agree.
 * Telling the Editor their layout value is not one of the recognised ones is a
 * successor story; here it simply resolves the way the renderer resolves it.
 */
export function layoutDisplaysMediaSlot(schema, scalars, slotKey) {
    const layout = schema.layout;
    if (!layout)
        return true;
    const chosen = scalars[layout.key] ?? '';
    const displayed = layout.displaysMediaSlots[chosen] ??
        layout.displaysMediaSlots[layout.fallback] ??
        [];
    return displayed.includes(slotKey);
}
/**
 * The label a field is shown under INSIDE a section.
 *
 * A section titled "Chapter 1" holding a field labelled "Chapter 1 · Eyebrow"
 * says the same words twice, and in a 456px rail the repetition is what makes
 * the label wrap. So the section's own prefix comes off — but only its own: a
 * sibling chapter's prefix is information, not noise.
 *
 * Only for display. {@link ScalarSlot.label} itself is untouched, because it is
 * also the field's name on three surfaces that have no heading to supply the
 * context: the canvas's placeholder text, the media library's crop-target list,
 * and the component previews. Stripping it at the source would make nine
 * distinct crop targets read as three identical pairs.
 */
function sectionFieldLabel(sectionLabel, slotLabel) {
    const prefix = `${sectionLabel} · `;
    if (!slotLabel.startsWith(prefix))
        return slotLabel;
    const stripped = slotLabel.slice(prefix.length);
    return stripped.length > 0 ? stripped : slotLabel;
}
/**
 * Group a component's slots the way its {@link SlotSchema.sections} say.
 *
 * Total by construction: every declared slot comes back exactly once, either in
 * a section or in the remainder. That is the property worth having — a slot
 * added to a schema and forgotten in `sections` appears in the wrong place
 * rather than nowhere, and a slot with no control is a slot an author cannot
 * fill in and has no way of discovering they cannot.
 *
 * A section whose keys all fail to resolve is dropped: an empty disclosure is
 * furniture that teaches authors the panel has nothing in it.
 */
export function resolveSlotSections(schema) {
    const documentSlots = schema.documentSlots ?? [];
    // Precedence follows the declaration order of the arrays on SlotSchema, for
    // the pathological case of one key appearing in two of them.
    const byKey = new Map();
    for (const slot of schema.mediaSlots)
        byKey.set(slot.key, { kind: 'media', slot, label: slot.label });
    for (const slot of documentSlots)
        byKey.set(slot.key, { kind: 'document', slot, label: slot.label });
    for (const slot of schema.scalarSlots)
        byKey.set(slot.key, { kind: 'scalar', slot, label: slot.label });
    const claimed = new Set();
    const sections = [];
    for (const section of schema.sections ?? []) {
        const slots = [];
        for (const key of section.slotKeys) {
            if (claimed.has(key))
                continue;
            const found = byKey.get(key);
            if (!found)
                continue;
            claimed.add(key);
            slots.push({
                ...found,
                label: sectionFieldLabel(section.label, found.slot.label),
            });
        }
        if (slots.length > 0) {
            sections.push({ key: section.key, label: section.label, slots });
        }
    }
    const unclaimed = (slots) => slots.filter((slot) => !claimed.has(slot.key));
    return {
        sections,
        remainder: {
            scalars: unclaimed(schema.scalarSlots),
            documents: unclaimed(documentSlots),
            media: unclaimed(schema.mediaSlots),
        },
    };
}
/**
 * Whether a slot will take this kind of media.
 *
 * The one place the default is applied. A slot that declares nothing takes
 * pictures — read through this rather than through `slot.accepts` directly, so
 * a caller cannot accidentally treat "undeclared" as "anything".
 */
export function slotAccepts(slot, mediaType) {
    return (slot.accepts ?? DEFAULT_ACCEPTS).includes(mediaType);
}
/**
 * The kind an id names, or undefined for an id this group does not offer.
 *
 * NO FALLBACK to the first option, deliberately and everywhere. Falling back
 * would turn a half-written add into the wrong item, and would make "a kind this
 * group does not know" unreachable as a refusal — the renderer and the validator
 * would silently disagree about what the item is.
 */
export function repeatVariantById(slot, id) {
    if (!slot.variants || !id)
        return undefined;
    return slot.variants.options.find((option) => option.id === id);
}
//# sourceMappingURL=slot-schema.js.map